import BlockIcon from "@mui/icons-material/Block";
import Logger from "../../services/Logger";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import TranslateIcon from "@mui/icons-material/Translate";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useLocation } from "react-router-dom";
import AddBannedWordDialog from "../setting/banned_words/AddBannedWordDialog";
import BannedService from "../setting/banned_words/BannedService";
import DictionaryApiService from "../system/integrate/DictionaryApiService";
import WiktionaryAttribution from "./WiktionaryAttribution";
import WordMeaningEditDialog from "./WordMeaningEditDialog";
import WordTranslationDialog from "./WordTranslationDialog";
import { usePagesTranslation } from "../../pages/locales";

/** Map getWordDefinition-shaped entry to a meaning object (POS → text) for WordTranslationDialog. */
const wordDataToMeaningObject = (entry) => {
    if (!entry?.meanings?.length) return null;
    const o = {};
    for (const m of entry.meanings) {
        const pos = m.partOfSpeech;
        if (!pos) continue;
        const text = (m.definitions || [])
            .map((d) => d.definition)
            .filter(Boolean)
            .join("\n\n");
        if (text) o[pos] = text;
    }
    return Object.keys(o).length ? o : null;
};

// Language to country code mapping
const LANGUAGE_COUNTRY_MAP = {
    english: 'US',
    french: 'FR',
    spanish: 'ES',
    german: 'DE',
    italian: 'IT',
    portuguese: 'BR',
    russian: 'RU',
    chinese: 'CN',
    japanese: 'JP',
    korean: 'KR'
};

export default function WordMeaning({
    selectedWord,
    onSaveDefinition,
    gridTplId,
    showClueButton = true,
    showBannedButton = true,
    fullHeight = false,
    /** When true, omit internal max-height/scroll; parent supplies `overflow-y: auto` (e.g. Words to Find). */
    scrollInParent = false,
    /** With `scrollInParent`, omit Paper padding so the parent can apply a single `p` (split panel). */
    scrollInParentUnpadded = false,
    hideWord = false,
    language = "english",
    showPronunciationAudio = false,
    /** When set, play UI shows translate (enabled when different from `language`, else disabled with tooltip) */
    uiLang = null,
    /** Play-only: show Translate … button (dictionary meanings); hide when false. */
    showTranslateButton = true,
}) {
    const { t } = usePagesTranslation();
    const location = useLocation();
    const isAdminPage = location.pathname.includes('/admin');
    const isCreatorPage = location.pathname.includes('/creator');
    const isPlayPage = !isAdminPage && !isCreatorPage;

    const normLang = (l) => (l == null ? "" : String(l).trim().toLowerCase());
    const getUiLabel = (lang) => {
        const k = normLang(lang);
        if (!k) return "";
        const path = `puzzle.wordMeaning.lang.${k}`;
        const label = t(path);
        if (label === path) return k.charAt(0).toUpperCase() + k.slice(1);
        return label;
    };
    const playTranslateButtonShown = Boolean(
        isPlayPage &&
            uiLang &&
            normLang(uiLang) &&
            showTranslateButton
    );
    const playTranslateActive = Boolean(
        playTranslateButtonShown &&
            normLang(language) &&
            normLang(uiLang) !== normLang(language)
    );
    
    const [wordData, setWordData] = useState(null);
    const [rawWordData, setRawWordData] = useState(null); // Store raw API response with meaningsI18n
    const [loading, setLoading] = useState(false);
    const [openBannedDialog, setOpenBannedDialog] = useState(false);
    const [selectedWordForBan, setSelectedWordForBan] = useState(null);
    const [isAddingBannedWord, setIsAddingBannedWord] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [translationDialogOpen, setTranslationDialogOpen] = useState(false);
    const [selectedTranslationLanguage, setSelectedTranslationLanguage] = useState(null);
    const [translationData, setTranslationData] = useState(null);
    const [translationLoading, setTranslationLoading] = useState(false);
    const [translationError, setTranslationError] = useState(null); // Track translation errors to prevent infinite retries
    const [pronunciationLoading, setPronunciationLoading] = useState(false);

    const wordForPronunciation = String(selectedWord || "").trim();

    const playPronunciation = async (wordOverride) => {
        const w = String(wordOverride || wordForPronunciation || "").trim();
        if (!w) return;
        setPronunciationLoading(true);
        try {
            const url = await DictionaryApiService.playWordAudio(w, language);
            if (!url) return;
            const audio = new Audio(url);
            await audio.play();
        } catch (e) {
            Logger.warn("[WordMeaning] pronunciation play failed:", e);
        } finally {
            setPronunciationLoading(false);
        }
    };

    const pronunciationIcon = (wordOverride) => {
        if (!showPronunciationAudio) return null;
        const w = String(wordOverride || wordForPronunciation || "").trim();
        if (!w) return null;
        return (
            <span>
                <IconButton
                    size="small"
                    onClick={() => playPronunciation(w)}
                    disabled={pronunciationLoading}
                    aria-label="Play pronunciation"
                    sx={{ color: "primary.main" }}
                >
                    {pronunciationLoading ? (
                        <CircularProgress size={20} sx={{ color: "primary.main" }} />
                    ) : (
                        <VolumeUpIcon fontSize="small" />
                    )}
                </IconButton>
            </span>
        );
    };

    // Fetch word definition when selectedWord changes
    useEffect(() => {
        const fetchWordDefinition = async () => {
            if (!selectedWord) {
                setWordData(null);
                setRawWordData(null);
                return;
            }

            setLoading(true);
            try {
                if (isAdminPage || isCreatorPage) {
                    // On admin or creator page, get raw response with meaningsI18n
                    const rawData = await DictionaryApiService.getWord(selectedWord, language);
                    setRawWordData(rawData);
                    
                    // Also get normalized data for display
                    const meaningData = await DictionaryApiService.getWordDefinition(selectedWord, language);
                    setWordData(meaningData);
                } else {
                    // On non-admin pages, use normalized data only
                    const meaningData = await DictionaryApiService.getWordDefinition(selectedWord, language);
                    setWordData(meaningData);
                    setRawWordData(null);
                }
            } catch (error) {
                Logger.error("[WordMeaning] Error fetching word meaning:", error);
                setWordData(null);
                setRawWordData(null);
                setSuccessMessage(`Failed to fetch definition for "${selectedWord}"`);
            } finally {
                setLoading(false);
            }
        };

        fetchWordDefinition();
    }, [selectedWord, language, isAdminPage, isCreatorPage]);

    // Auto-fetch translation if dialog is open but translationData doesn't exist or doesn't have Korean
    useEffect(() => {
        const fetchTranslationIfNeeded = async () => {
            if (!translationDialogOpen || !selectedWord || !rawWordData || selectedTranslationLanguage !== 'korean') {
                return;
            }

            // Don't retry if there was a previous error for this word/language combination
            if (translationError) {
                return;
            }

            // Check if translationData exists and has Korean translation
            const hasKoreanTranslation = translationData?.meaningsI18n?.korean;
            
            if (!translationData || !hasKoreanTranslation) {
                setTranslationLoading(true);
                setTranslationError(null); // Clear previous error
                try {
                    const translatedData = await DictionaryApiService.translateWord(
                        selectedWord,
                        language,
                        'korean'
                    );
                    setTranslationData(translatedData);
                    // Update rawWordData with new translation
                    if (translatedData.meaningsI18n && translatedData.meaningsI18n.korean) {
                        setRawWordData(prev => ({
                            ...prev,
                            meaningsI18n: {
                                ...prev?.meaningsI18n,
                                ...translatedData.meaningsI18n
                            }
                        }));
                    }
                    setTranslationLoading(false);
                } catch (error) {
                    Logger.error('Error fetching Korean translation:', error);
                    setSuccessMessage(`Failed to fetch Korean translation: ${error.message || 'Unknown error'}`);
                    setTranslationError(error.message || 'Translation failed'); // Set error to prevent retries
                    setTranslationLoading(false);
                    setTranslationData(null); // Clear translation data on error
                }
            }
        };

        fetchTranslationIfNeeded();
    }, [translationDialogOpen, selectedWord, rawWordData, translationData, selectedTranslationLanguage, language, translationError]);

    const handleSaveTitle = (definition, word) => {
        if (onSaveDefinition && gridTplId) {
            onSaveDefinition(definition, word, gridTplId);
        }
    };

    const handleAddBannedWord = (word) => {
        setSelectedWordForBan(word);
        setOpenBannedDialog(true);
    };

    const handleAddBannedWordToDatabase = async (word, category, language) => {
        try {
            setIsAddingBannedWord(true);
            await BannedService.addWord(category, word, "", language);
            // Logger.log(`Successfully added "${word}" to banned words list with category "${category}"`);
            setSuccessMessage(`"${word}" has been added to the banned words list`);
            setOpenBannedDialog(false);
            setSelectedWordForBan(null);
        } catch (error) {
            Logger.error('Error adding banned word:', error);
            setSuccessMessage(error?.message ?? `Error adding "${word}" to banned words list`);
        } finally {
            setIsAddingBannedWord(false);
        }
    };

    const handleCloseBannedDialog = () => {
        setOpenBannedDialog(false);
        setSelectedWordForBan(null);
    };

    const handleOpenEditDialog = () => {
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
    };

    const handleSaveMeaning = async () => {
        // Refresh the word data after saving
        if (selectedWord) {
            try {
                if (isAdminPage || isCreatorPage) {
                    const rawData = await DictionaryApiService.getWord(selectedWord, language);
                    setRawWordData(rawData);
                }
                const meaningData = await DictionaryApiService.getWordDefinition(selectedWord, language);
                setWordData(meaningData);
                setSuccessMessage(`Successfully updated meaning for "${selectedWord}"`);
            } catch (error) {
                Logger.error("Error refreshing word meaning:", error);
                setSuccessMessage(`Failed to refresh definition for "${selectedWord}"`);
            }
        }
    };

    const handleFlagClick = async (targetLanguage) => {
        if (!selectedWord) {
            return;
        }

        // Clear previous translation error when opening dialog for a new language
        setTranslationError(null);

        if (isPlayPage && !rawWordData) {
            const playOriginal = wordDataToMeaningObject(wordData?.[0]);
            setSelectedTranslationLanguage(targetLanguage);
            setTranslationDialogOpen(true);
            setTranslationLoading(true);
            setTranslationData(null);
            setRawWordData(
                playOriginal && Object.keys(playOriginal).length > 0
                    ? { meaning: playOriginal }
                    : { meaning: null }
            );
            try {
                const translatedData = await DictionaryApiService.translateWord(
                    selectedWord,
                    normLang(language) || "english",
                    targetLanguage
                );
                if (translatedData && (translatedData.error || translatedData.code === "FAILURE")) {
                    const msg =
                        (typeof translatedData.error === "string" && translatedData.error) ||
                        translatedData.message ||
                        t("puzzle.wordMeaning.translation-failed");
                    setTranslationError(msg);
                    setTranslationData(null);
                } else {
                    setTranslationData(translatedData);
                    if (translatedData?.meaningsI18n?.[targetLanguage]) {
                        setRawWordData((prev) => ({
                            ...prev,
                            meaningsI18n: {
                                ...prev?.meaningsI18n,
                                ...translatedData.meaningsI18n
                            }
                        }));
                    }
                }
            } catch (error) {
                Logger.error("[WordMeaning] play translation:", error);
                setTranslationError(error?.message || t("puzzle.wordMeaning.translation-failed"));
                setTranslationData(null);
            } finally {
                setTranslationLoading(false);
            }
            return;
        }

        if (!rawWordData) {
            // Try to fetch rawWordData if it doesn't exist
            try {
                const rawData = await DictionaryApiService.getWord(selectedWord, language);
                setRawWordData(rawData);
                
                // Continue with the translation flow
                setSelectedTranslationLanguage(targetLanguage);
                setTranslationDialogOpen(true);
                
                // Check if translation already exists in meaningsI18n
                if (rawData.meaningsI18n && rawData.meaningsI18n[targetLanguage]) {
                    setTranslationData(rawData);
                    setTranslationLoading(false);
                } else {
                    // Translation doesn't exist, fetch it
                    setTranslationLoading(true);
                    setTranslationData(null);
                    setTranslationError(null);
                    
                    try {
                        const translatedData = await DictionaryApiService.translateWord(
                            selectedWord,
                            language,
                            targetLanguage
                        );
                        setTranslationData(translatedData);
                        
                        // Update rawWordData with new translation
                        if (translatedData.meaningsI18n && translatedData.meaningsI18n[targetLanguage]) {
                            setRawWordData(prev => ({
                                ...prev,
                                meaningsI18n: {
                                    ...prev?.meaningsI18n,
                                    ...translatedData.meaningsI18n
                                }
                            }));
                        }
                        setTranslationLoading(false);
                    } catch (error) {
                        Logger.error(`Error fetching translation to ${targetLanguage}:`, error);
                        setSuccessMessage(`Failed to fetch translation to ${targetLanguage}: ${error.message || 'Unknown error'}`);
                        setTranslationError(error.message || 'Translation failed');
                        setTranslationLoading(false);
                        setTranslationData(null);
                    }
                }
            } catch (error) {
                Logger.error(`Error fetching translation to ${targetLanguage}:`, error);
                setSuccessMessage(`Failed to fetch translation to ${targetLanguage}: ${error.message || 'Unknown error'}`);
                setTranslationLoading(false);
                setTranslationError(error.message || 'Translation failed');
            }
            return;
        }

        setSelectedTranslationLanguage(targetLanguage);
        setTranslationDialogOpen(true);

        // Check if translation already exists in meaningsI18n
        if (rawWordData.meaningsI18n && rawWordData.meaningsI18n[targetLanguage]) {
            setTranslationData(rawWordData);
            setTranslationLoading(false);
        } else {
            // Translation doesn't exist, fetch it
            setTranslationLoading(true);
            setTranslationData(null); // Clear previous translation data
            setTranslationError(null); // Clear previous error
            
            try {
                const translatedData = await DictionaryApiService.translateWord(
                    selectedWord,
                    language,
                    targetLanguage
                );
                setTranslationData(translatedData);
                // Update rawWordData with new translation
                if (translatedData.meaningsI18n && translatedData.meaningsI18n[targetLanguage]) {
                    setRawWordData(prev => ({
                        ...prev,
                        meaningsI18n: {
                            ...prev?.meaningsI18n,
                            ...translatedData.meaningsI18n
                        }
                    }));
                }
                setTranslationLoading(false);
            } catch (error) {
                Logger.error(`[WordMeaning] Error fetching translation to ${targetLanguage}:`, error);
                setSuccessMessage(`Failed to fetch translation to ${targetLanguage}: ${error.message || 'Unknown error'}`);
                setTranslationError(error.message || 'Translation failed');
                setTranslationLoading(false);
                setTranslationData(null);
            }
        }
    };

    const handleCloseTranslationDialog = () => {
        setTranslationDialogOpen(false);
        setSelectedTranslationLanguage(null);
        setTranslationError(null); // Clear error when closing dialog
        setTranslationData(null);
    };

    // Container style: scrollInParent lets the parent panel scroll; fullHeight fills a bounded flex slot
    const containerSx = scrollInParent
        ? {
              p: scrollInParentUnpadded ? 0 : 2,
              width: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              overflow: 'visible',
          }
        : fullHeight
          ? {
                p: 2,
                height: '100%',
                width: '100%',
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                elevation: 0,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                flex: '1 1 auto',
                boxSizing: 'border-box',
            }
          : { p: 2, maxHeight: 400, overflow: 'auto' };

    const flatPaper = scrollInParent || fullHeight;

    if (loading) {
        return (
            <Paper
                elevation={flatPaper ? 0 : 2}
                sx={{
                    p: 2,
                    mt: flatPaper ? 0 : 2,
                    ...(flatPaper && { boxShadow: 'none', backgroundColor: 'transparent' }),
                    ...(fullHeight && !scrollInParent && { height: '100%' }),
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Loading definition...
                </Typography>
            </Paper>
        );
    }

    if (!wordData || wordData.length === 0) {
        return (
            <Paper elevation={flatPaper ? 0 : 2} sx={containerSx}>
                {showPronunciationAudio && wordForPronunciation && !hideWord ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <Typography variant="h6" sx={{ color: "primary.main", m: 0 }}>
                            {wordForPronunciation}
                        </Typography>
                        {pronunciationIcon(wordForPronunciation)}
                    </Box>
                ) : null}
                <Typography variant="h6" gutterBottom sx={{ color: 'warning.main' }}>
                    No Meaning Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    There is no meaning available for this word. The word might be:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                    • A proper noun (name, place)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    • A very rare or uncommon word
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    • An abbreviation or acronym
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    • Not in the dictionary database
                </Typography>
            </Paper>
        );
    }

    const wordEntry = wordData[0]; // Use the first entry

    // Check if the word entry has meanings
    if (wordEntry.meanings === undefined || wordEntry.meanings === null || wordEntry.meanings.length === 0) {
        return (
            <Paper elevation={flatPaper ? 0 : 2} sx={containerSx}>
                {!hideWord && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6" sx={{ color: 'warning.main', m: 0 }}>
                            {wordEntry.word || 'Unknown Word: ' + selectedWord}
                        </Typography>
                        {pronunciationIcon(wordEntry.word || selectedWord)}
                    </Box>
                )}
                <Typography variant="body2" color="text.secondary">
                    No definitions found for this word in the dictionary.
                </Typography>
            </Paper>
        );
    }

    return (
        <>
            <Paper elevation={flatPaper ? 0 : 2} sx={containerSx}>
                {!hideWord && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                            <Typography variant="h6" sx={{ color: 'primary.main', m: 0 }}>
                                {wordEntry.word}
                            </Typography>
                            {pronunciationIcon(wordEntry.word || selectedWord)}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {(isAdminPage || isCreatorPage) && (
                                <IconButton
                                    size="small"
                                    onClick={() => handleFlagClick('korean')}
                                    sx={{
                                        padding: '2px',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            transform: 'scale(1.1)'
                                        },
                                        transition: 'transform 0.2s'
                                    }}
                                >
                                    <ReactCountryFlag
                                        countryCode="KR"
                                        svg
                                        style={{
                                            width: '1.2em',
                                            height: '1.2em'
                                        }}
                                    />
                                </IconButton>
                            )}
                            {isAdminPage && (
                                <IconButton
                                    size="small"
                                    onClick={handleOpenEditDialog}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.light',
                                            color: 'primary.dark'
                                        }
                                    }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                            {isPlayPage && playTranslateButtonShown && (
                                <Tooltip
                                    title={!playTranslateActive ? t("puzzle.wordMeaning.translate-same-lang") : ""}
                                    disableHoverListener={playTranslateActive}
                                >
                                    <span>
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleFlagClick(normLang(uiLang))}
                                            disabled={translationLoading || !playTranslateActive}
                                            startIcon={
                                                translationLoading ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    <TranslateIcon sx={{ fontSize: 18 }} />
                                                )
                                            }
                                            sx={{ textTransform: "none", borderColor: "#3BA666", color: "#2E8B57" }}
                                        >
                                            {translationLoading
                                                ? t("puzzle.wordMeaning.translating")
                                                : t("puzzle.wordMeaning.translate", { language: getUiLabel(uiLang) })}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )}

                {hideWord && isPlayPage && playTranslateButtonShown && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                        <Tooltip
                            title={!playTranslateActive ? t("puzzle.wordMeaning.translate-same-lang") : ""}
                            disableHoverListener={playTranslateActive}
                        >
                            <span>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleFlagClick(normLang(uiLang))}
                                    disabled={translationLoading || !playTranslateActive}
                                    startIcon={
                                        translationLoading ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <TranslateIcon sx={{ fontSize: 18 }} />
                                        )
                                    }
                                    sx={{ textTransform: "none", borderColor: "#3BA666", color: "#2E8B57" }}
                                >
                                    {translationLoading
                                        ? t("puzzle.wordMeaning.translating")
                                        : t("puzzle.wordMeaning.translate", { language: getUiLabel(uiLang) })}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}

                {wordEntry.phonetics && wordEntry.phonetics.length > 0 && wordEntry.phonetics[0].text && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                        {wordEntry.phonetics[0].text}
                    </Typography>
                )}

                {wordEntry?.meanings.map((meaning, meaningIndex) => (
                    <Box key={meaningIndex} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 1 }}>
                            {meaning.partOfSpeech}
                        </Typography>

                        {meaning.definitions.map((def, defIndex) => (
                            <Box key={defIndex} sx={{ mb: 1.5, pl: 1 }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    '&:hover .save-icon': {
                                        opacity: 1
                                    }
                                }}>
                                    <Box sx={{ flex: 1, pr: 1 }}>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                mb: 0.5, 
                                                whiteSpace: 'pre-line',
                                                lineHeight: 1.6,
                                                color: 'text.primary'
                                            }}
                                        >
                                            {def.definition?.replace(/(\d+\.\s)/g, '\n$1').trim()}
                                        </Typography>
                                        {def.example && (
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    fontStyle: 'italic', 
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    mt: 0.5,
                                                    pl: 1,
                                                    borderLeft: '2px solid',
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                "{def.example}"
                                            </Typography>
                                        )}
                                        {def.synonyms && def.synonyms.length > 0 && (
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    display: 'block', 
                                                    color: 'text.secondary', 
                                                    mt: 0.5,
                                                    fontSize: '0.7rem'
                                                }}
                                            >
                                                <Box component="span" sx={{ fontWeight: 600 }}>Synonyms:</Box> {def.synonyms.join(', ')}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {showClueButton && (
                                            <IconButton
                                                className="save-icon"
                                                size="small"
                                                onClick={() => handleSaveTitle(def.definition, wordEntry.word)}
                                                sx={{
                                                    opacity: 1,
                                                    transition: 'color 0.2s, background-color 0.2s',
                                                    color: 'grey.400',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.light',
                                                        color: 'primary.main'
                                                    }
                                                }}
                                            >
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        {showBannedButton && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleAddBannedWord(wordEntry.word)}
                                                sx={{
                                                    opacity: 1,
                                                    transition: 'color 0.2s, background-color 0.2s',
                                                    color: 'error.400',
                                                    '&:hover': {
                                                        backgroundColor: 'error.light',
                                                        color: 'error.main'
                                                    }
                                                }}
                                            >
                                                <BlockIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                                {defIndex < meaning.definitions.length - 1 && (
                                    <Divider sx={{ my: 0.5, opacity: 0.3 }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                ))}

                {/* Wiktionary License Attribution */}
                <WiktionaryAttribution />
            </Paper>

            {/* Add Banned Word Dialog */}
            <AddBannedWordDialog
                open={openBannedDialog}
                onClose={handleCloseBannedDialog}
                onAdd={handleAddBannedWordToDatabase}
                selectedWord={selectedWordForBan}
                loading={isAddingBannedWord}
                language={language}
            />

            {/* Edit Word Meaning Dialog - Only show on admin pages */}
            {isAdminPage && (
                <WordMeaningEditDialog
                    open={openEditDialog}
                    onClose={handleCloseEditDialog}
                    word={selectedWord}
                    language={language}
                    onSave={handleSaveMeaning}
                />
            )}

            {/* Translation dialog — admin (Korean flag) or play (UI language) */}
            {((isAdminPage || isCreatorPage) || playTranslateButtonShown) && (
                <WordTranslationDialog
                    open={translationDialogOpen}
                    onClose={handleCloseTranslationDialog}
                    word={selectedWord}
                    language={language}
                    translationData={translationData}
                    originalMeaning={rawWordData?.meaning}
                    loading={translationLoading}
                    targetLanguage={selectedTranslationLanguage || "korean"}
                    targetColumnTitle={t("puzzle.wordMeaning.translation-heading", {
                        language: getUiLabel(selectedTranslationLanguage || "korean")
                    })}
                />
            )}

            {/* Success/Error Message Snackbar */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={() => setSuccessMessage("")}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSuccessMessage("")}
                    severity={successMessage.includes("Error") || successMessage.includes("Failed") ? "error" : "success"}
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

